import { useEffect, useState, useMemo } from 'react';
import Overview from './components/Overview';
import ChainMap from './components/ChainMap';
import CompanyRanking from './components/CompanyRanking';
import QuadrantChart from './components/QuadrantChart';
import { useIsMobile } from './hooks/useIsMobile';
import { useDataStore } from './stores/dataStore';
import { useSelectionStore, type ChainKey } from './stores/selectionStore';
import { useExplorationStore } from './stores/explorationStore';

type Page = 'overview' | 'chain-map' | 'ranking' | 'quadrant' | 'notes';

// 货币符号映射（与 DecisionBar 保持一致）
const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', HKD: 'HK$', TWD: 'NT$', JPY: '¥', KRW: '₩', EUR: '€', CNY: '¥',
};
// 交易所 → 货币映射（旧笔记回填用）
const CURRENCY_BY_EXCHANGE: Record<string, string> = {
  '美股': 'USD', '港股': 'HKD', '台股': 'TWD',
  '日股': 'JPY', '韩股': 'KRW', '欧股': 'EUR',
};

const PAGE_LABELS: Record<Page, string> = {
  'overview': '概览',
  'chain-map': '地图',
  'ranking': '排名',
  'quadrant': '象限',
  'notes': '笔记',
};

function App() {
  const [page, setPage] = useState<Page>('overview');
  const isMobile = useIsMobile();

  // 全局状态
  const focusedChain = useSelectionStore(s => s.focusedChain);
  const setChain = useSelectionStore(s => s.setChain);
  const clearSelection = useSelectionStore(s => s.clearSelection);
  const hasSelection = useSelectionStore(s => !!(s.selectedNodeId || s.selectedCompanyId));
  const selectedNode = useSelectionStore(s => s.selectedNodeId);
  const selectedCompany = useSelectionStore(s => s.selectedCompanyId);
  const selectNode = useSelectionStore(s => s.selectNode);
  const selectCompany = useSelectionStore(s => s.selectCompany);
  const convictionCount = useExplorationStore(s => s.notes.filter(n => n.status === 'conviction').length);
  const watchlistCount = useExplorationStore(s => s.notes.filter(n => n.status === 'watchlist').length);
  const { loaded, error, loadAll } = useDataStore();

  // 顶层加载数据（一次加载，4 页面共享）
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Esc 退出聚焦
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && hasSelection) clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasSelection, clearSelection]);

  // ===== 4C-Lite: URL 参数可分享 =====
  // 读取 URL 参数（?node=xxx 或 ?company=xxx）— React 19 渲染期同步模式
  const [urlInitialized, setUrlInitialized] = useState(false);
  if (!urlInitialized && loaded) {
    setUrlInitialized(true);
    const params = new URLSearchParams(window.location.search);
    const nodeParam = params.get('node');
    const companyParam = params.get('company');
    if (nodeParam) {
      selectNode(nodeParam);
      setPage('chain-map');
    } else if (companyParam) {
      selectCompany(companyParam);
      setPage('ranking');
    }
  }

  // 选中变化时更新 URL（不刷新页面）— React 19 渲染期同步模式
  const [prevSelection, setPrevSelection] = useState(`${selectedNode ?? ''}|${selectedCompany ?? ''}`);
  const curSelection = `${selectedNode ?? ''}|${selectedCompany ?? ''}`;
  if (prevSelection !== curSelection && urlInitialized) {
    setPrevSelection(curSelection);
    const params = new URLSearchParams();
    if (selectedNode) params.set('node', selectedNode);
    if (selectedCompany) params.set('company', selectedCompany);
    const newSearch = params.toString();
    const currentSearch = window.location.search;
    if (newSearch !== currentSearch) {
      window.history.replaceState(null, '', newSearch ? `?${newSearch}` : window.location.pathname);
    }
  }

  if (!loaded) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', background: '#0f172a', height: '100vh' }}>加载产业链数据中…</div>;
  }
  if (error) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#ef4444', background: '#0f172a', height: '100vh' }}>加载失败：{error}</div>;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#0f172a' }}>
      <header style={{
        padding: isMobile ? '8px 12px' : '12px 24px',
        borderBottom: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '24px',
        flexWrap: 'wrap',
        background: '#0b1220',
      }}>
        <h1 style={{ fontSize: isMobile ? '14px' : '18px', margin: 0, whiteSpace: 'nowrap', color: '#f1f5f9' }}>
          AI产业链地图
        </h1>
        <nav style={{ display: 'flex', gap: isMobile ? '6px' : '12px', flexWrap: 'wrap' }}>
          {(['overview', 'chain-map', 'ranking', 'quadrant', 'notes'] as Page[]).map(p => {
            const isActive = page === p;
            const totalNotes = convictionCount + watchlistCount;
            const showBadge = p === 'notes' && totalNotes > 0;
            return (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  padding: isMobile ? '4px 10px' : '6px 16px',
                  border: isActive ? '2px solid #3b82f6' : '1px solid #334155',
                  borderRadius: '6px',
                  background: isActive ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: isActive ? '#93c5fd' : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: isMobile ? '12px' : '14px',
                  position: 'relative',
                  fontWeight: isActive ? 700 : 500,
                }}
              >
                {PAGE_LABELS[p]}
                {/* 单徽标：conviction/total 斜杠分隔，conviction 金色 + watchlist 绿色 */}
                {showBadge && (
                  <span style={{
                    position: 'absolute', top: isMobile ? -4 : -6, right: isMobile ? -4 : -6,
                    background: '#0b1220', borderRadius: 8,
                    fontSize: isMobile ? 9 : 10, fontWeight: 700,
                    minWidth: 18, height: 16, padding: '0 4px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                    border: '1px solid #334155',
                  }}>
                    <span style={{ color: '#fbbf24' }}>{convictionCount}</span>
                    <span style={{ color: '#475569', margin: '0 1px' }}>/</span>
                    <span style={{ color: '#10b981' }}>{watchlistCount}</span>
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: isMobile ? '4px' : '8px', alignItems: 'center' }}>
          {isMobile ? (
            // 移动端：4 个纯色色块，颜色即身份（N=绿/G=蓝/H=红/全局=灰），比 select 更省空间且保留语义
            (['all', 'nvidia', 'google', 'huawei'] as ChainKey[]).map(chain => {
              const color = chain === 'nvidia' ? '#16a34a' : chain === 'google' ? '#2563eb' : chain === 'huawei' ? '#dc2626' : '#64748b';
              const label = chain === 'all' ? '全' : chain === 'nvidia' ? 'N' : chain === 'google' ? 'G' : 'H';
              const isActive = focusedChain === chain;
              return (
                <button
                  key={chain}
                  onClick={() => setChain(chain)}
                  aria-label={chain === 'all' ? '全局视图' : chain === 'nvidia' ? '英伟达链' : chain === 'google' ? '谷歌链' : '华为链'}
                  aria-pressed={isActive}
                  style={{
                    width: 32, height: 32, borderRadius: 6,
                    border: isActive ? `2px solid ${color}` : '2px solid transparent',
                    background: isActive ? color : `${color}30`,
                    color: isActive ? '#fff' : color,
                    cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              );
            })
          ) : (
            ['all', 'nvidia', 'google', 'huawei'].map(chain => (
              <button
                key={chain}
                onClick={() => setChain(chain as ChainKey)}
                style={{
                  padding: '4px 12px',
                  border: focusedChain === chain ? `2px solid ${chain === 'nvidia' ? '#16a34a' : chain === 'google' ? '#2563eb' : chain === 'huawei' ? '#dc2626' : '#10b981'}` : '1px solid #334155',
                  borderRadius: '4px',
                  background: focusedChain === chain
                    ? (chain === 'nvidia' ? 'rgba(22,163,74,0.15)' : chain === 'google' ? 'rgba(37,99,235,0.15)' : chain === 'huawei' ? 'rgba(220,38,38,0.15)' : 'rgba(16,185,129,0.15)')
                    : 'transparent',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: focusedChain === chain ? 700 : 400,
                  color: focusedChain === chain
                    ? chain === 'nvidia' ? '#4ade80' : chain === 'google' ? '#60a5fa' : chain === 'huawei' ? '#f87171' : '#34d399'
                    : '#94a3b8',
                }}
              >
                {chain === 'all' ? '全局' : chain === 'nvidia' ? '英伟达链' : chain === 'google' ? '谷歌链' : '华为链'}
              </button>
            ))
          )}
        </div>
      </header>
      <main style={{ flex: 1, overflow: 'hidden' }}>
        {page === 'overview' && <Overview chain={focusedChain} />}
        {page === 'chain-map' && <ChainMap chain={focusedChain} />}
        {page === 'ranking' && <CompanyRanking chain={focusedChain} />}
        {page === 'quadrant' && <QuadrantChart chain={focusedChain} />}
        {page === 'notes' && (
          <NotesPage
            onNavigateToNode={(nodeId, chain) => {
              setChain(chain as ChainKey);
              selectNode(nodeId);
              setPage('chain-map');
            }}
            onNavigateToCompany={(companyId) => {
              selectCompany(companyId);
              setPage('ranking');
            }}
          />
        )}
      </main>
    </div>
  );
}

// ===== NotesPage — 个人投研笔记时间轴 =====
function NotesPage({
  onNavigateToNode,
  onNavigateToCompany,
}: {
  onNavigateToNode: (nodeId: string, chain: string) => void;
  onNavigateToCompany: (companyId: string) => void;
}) {
  const { companyIndex, nodeIndex } = useDataStore();
  const notes = useExplorationStore(s => s.notes);
  const resetAll = useExplorationStore(s => s.resetAll);
  const isMobile = useIsMobile();

  // 排序放到 useMemo，避免每次渲染产生新引用
  const allNotes = useMemo(() => [...notes].sort((a, b) => b.timestamp - a.timestamp), [notes]);

  const formatDate = (ts: number) =>
    new Date(ts).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getEntityName = (entityId: string, entityType: string) => {
    if (entityType === 'node') {
      return nodeIndex.get(entityId)?.name ?? entityId;
    }
    return companyIndex.get(entityId)?.company ?? entityId;
  };

  const convictionNotes = allNotes.filter(n => n.status === 'conviction');
  const watchlistNotes = allNotes.filter(n => n.status === 'watchlist');

  // 确信胜率聚合（仅公司笔记 + 有 stockPrice + 有当前价 + 同币种）
  const priceTrackedConvictions = convictionNotes.filter(n => {
    if (n.entityType !== 'company' || !n.stockPrice) return false;
    const c = companyIndex.get(n.entityId);
    return c?.latest_close != null;
  });
  const winCount = priceTrackedConvictions.filter(n => {
    const c = companyIndex.get(n.entityId);
    return c!.latest_close! > n.stockPrice!;
  }).length;
  const avgReturn = priceTrackedConvictions.length > 0
    ? priceTrackedConvictions.reduce((sum, n) => {
      const c = companyIndex.get(n.entityId);
      return sum + ((c!.latest_close! - n.stockPrice!) / n.stockPrice!) * 100;
    }, 0) / priceTrackedConvictions.length
    : null;

  if (allNotes.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', marginTop: 80, background: '#0f172a', height: '100%' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>📝</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>
          你的投研笔记将出现在这里
        </div>
        <div style={{ fontSize: 13, color: '#94a3b8' }}>
          在地图中查看节点，在详情面板记录你的判断和理由
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: isMobile ? '12px 12px' : '16px 24px', background: '#0f172a' }}>
      {/* 统计卡片 */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 70 : 90}px, 1fr))`, gap: isMobile ? 8 : 12, marginBottom: 20 }}>
        <div style={{ padding: 12, borderRadius: 8, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#fbbf24' }}>{convictionNotes.length}</div>
          <div style={{ fontSize: 11, color: '#92400e' }}>确信笔记</div>
        </div>
        <div style={{ padding: 12, borderRadius: 8, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#10b981' }}>{watchlistNotes.length}</div>
          <div style={{ fontSize: 11, color: '#065f46' }}>观察中</div>
        </div>
        <div style={{ padding: 12, borderRadius: 8, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>{allNotes.length}</div>
          <div style={{ fontSize: 11, color: '#1e40af' }}>总笔记数</div>
        </div>
        {priceTrackedConvictions.length > 0 && (
          <div style={{
            padding: 12, borderRadius: 8,
            background: avgReturn != null && avgReturn >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${avgReturn != null && avgReturn >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            ...(isMobile ? { gridColumn: 'span 2' } : {}),
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: avgReturn != null && avgReturn >= 0 ? '#22c55e' : '#ef4444' }}>
              {winCount}/{priceTrackedConvictions.length}
            </div>
            <div style={{ fontSize: 11, color: avgReturn != null && avgReturn >= 0 ? '#166534' : '#991b1b' }}>
              确信胜率{avgReturn != null ? ` · 均${avgReturn >= 0 ? '+' : ''}${avgReturn.toFixed(1)}%` : ''}
            </div>
          </div>
        )}
      </div>

      {/* 清空按钮 */}
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <button onClick={() => { if (confirm('确定清空所有笔记？此操作不可恢复。')) resetAll(); }}
          style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #334155', background: 'transparent', color: '#64748b', fontSize: 11, cursor: 'pointer' }}>
          清空所有笔记
        </button>
      </div>

      {/* 笔记时间轴 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {allNotes.map(note => {
          const entityName = getEntityName(note.entityId, note.entityType);
          const statusColor = note.status === 'conviction' ? '#fbbf24' : note.status === 'watchlist' ? '#10b981' : '#64748b';
          const statusLabel = note.status === 'conviction' ? '◆ 确信' : note.status === 'watchlist' ? '● 观察' : '— 跳过';
          const entityBadge = note.entityType === 'node' ? '📍 节点' : '🏢 公司';
          const getTagEmoji = (t: string) => t === 'catalyst' ? '🚀' : t === 'valuation' ? '📊' : t === 'tech_lead' ? '⚡' : t === 'industry_position' ? '🏰' : '';

          // 股价对比（仅公司 conviction 笔记且有 stockPrice 时显示）
          const company = note.entityType === 'company' ? companyIndex.get(note.entityId) : undefined;
          const currentPrice = company?.latest_close;
          const latestDate = company?.latest_date;
          // 旧笔记回填：note.stockPriceCurrency 缺失时从公司 exchange 推断
          const currency = note.stockPriceCurrency
            ?? (company ? (CURRENCY_BY_EXCHANGE[company.exchange] ?? 'CNY') : 'CNY');
          const currencySymbol = CURRENCY_SYMBOL[currency] ?? '¥';
          const hasPriceComparison = note.status === 'conviction' && note.stockPrice && currentPrice;
          const priceChange = hasPriceComparison
            ? ((currentPrice! - note.stockPrice!) / note.stockPrice!) * 100
            : null;

          return (
            <div
              key={note.id}
              onClick={() => {
                if (note.entityType === 'node') {
                  const node = nodeIndex.get(note.entityId);
                  const chain = node?.chains?.[0] ?? 'all';
                  onNavigateToNode(note.entityId, chain);
                } else {
                  onNavigateToCompany(note.entityId);
                }
              }}
              style={{
                padding: 14, borderRadius: 8, cursor: 'pointer',
                background: 'rgba(15,23,42,0.6)',
                border: `1px solid ${note.status === 'conviction' ? 'rgba(251,191,36,0.25)' : note.status === 'watchlist' ? 'rgba(16,185,129,0.25)' : '#1e293b'}`,
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(15,23,42,0.85)';
                (e.currentTarget as HTMLDivElement).style.borderColor = note.status === 'conviction' ? 'rgba(251,191,36,0.5)' : note.status === 'watchlist' ? 'rgba(16,185,129,0.5)' : '#334155';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(15,23,42,0.6)';
                (e.currentTarget as HTMLDivElement).style.borderColor = note.status === 'conviction' ? 'rgba(251,191,36,0.25)' : note.status === 'watchlist' ? 'rgba(16,185,129,0.25)' : '#1e293b';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: 10, background: 'rgba(59,130,246,0.15)', color: '#93c5fd', padding: '1px 5px', borderRadius: 3, flexShrink: 0 }}>
                    {entityBadge}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entityName}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, background: `${statusColor}20`, padding: '1px 5px', borderRadius: 3, flexShrink: 0 }}>
                    {statusLabel}
                  </span>
                  {note.tags.map(t => (
                    <span key={t} style={{ fontSize: 12, flexShrink: 0 }}>{getTagEmoji(t)}</span>
                  ))}
                </div>
                <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {formatDate(note.timestamp)}
                </span>
              </div>
              {note.reason && (
                <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 }}>{note.reason}</div>
              )}
              {/* 股价追踪 — 记录时 vs 当前 */}
              {hasPriceComparison && (
                <div style={{
                  marginTop: 6, paddingTop: 6, borderTop: '1px solid #1e293b',
                  display: 'flex', gap: 8, alignItems: 'center', fontSize: 10, flexWrap: 'wrap',
                }}>
                  <span style={{ color: '#64748b' }}>
                    记录时 {currencySymbol}{note.stockPrice!.toFixed(2)}
                  </span>
                  <span style={{ color: '#64748b' }}>→</span>
                  <span style={{ color: '#94a3b8', fontWeight: 600 }}>
                    现 {currencySymbol}{currentPrice!.toFixed(2)}
                  </span>
                  <span style={{
                    fontWeight: 700,
                    color: priceChange! >= 0 ? '#22c55e' : '#ef4444',
                    background: priceChange! >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                    padding: '1px 6px', borderRadius: 3,
                  }}>
                    {priceChange! >= 0 ? '+' : ''}{priceChange!.toFixed(1)}%
                  </span>
                  {latestDate && (
                    <span style={{ color: '#475569', fontSize: 9 }}>截至 {latestDate}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default App;
